import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { Article } from '../types';

interface MapComponentProps {
  articles: Article[];
  onArticleHover?: (article: Article | null) => void;
  onArticleClick?: (article: Article) => void;
  onArticleNavigate?: (article: Article) => void;
  selectedArticle?: Article | null;
}

const MapComponent: React.FC<MapComponentProps> = ({ 
  articles, 
  onArticleHover, 
  onArticleClick,
  onArticleNavigate,
  selectedArticle 
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (!mapRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Initialize map
      const map = L.map(mapRef.current!).setView([40.0, 0.0], 2);
    
      // Add tile layer with English place names
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 18,
      }).addTo(map);

      mapInstanceRef.current = map;
      
      // Force map to recalculate its size multiple times to ensure tiles load
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
          mapInstanceRef.current.panBy([0, 0]); // Force a tiny pan to trigger tile loading
        }
      }, 200);
      
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 500);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log('MapComponent useEffect - articles:', articles.length);
    if (!mapInstanceRef.current) {
      console.log('No map instance yet');
      // Wait a bit and try again
      const retryTimer = setTimeout(() => {
        if (mapInstanceRef.current && articles.length > 0) {
          // Trigger this effect again by calling it directly
          console.log('Retrying marker creation...');
          createMarkers();
        }
      }, 1000);
      return () => clearTimeout(retryTimer);
    }
    
    createMarkers();
  }, [articles, selectedArticle, onArticleHover, onArticleClick, onArticleNavigate]);

  const createMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapInstanceRef.current?.removeLayer(marker);
    });
    markersRef.current = [];

    // Create simple marker icon for now
    const createCustomIcon = (isSelected: boolean = false) => {
      return L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [0, -42],
        shadowSize: [41, 41]
      });
    };

    // Group articles by location
    const locationGroups = new Map<string, Article[]>();
    articles.forEach(article => {
      console.log('Article:', article.title, 'Lat:', article.location_lat, 'Lng:', article.location_lng, 'Location:', article.location_name);
      if (article.location_lat && article.location_lng && article.location_name) {
        const key = `${article.location_lat},${article.location_lng}`;
        if (!locationGroups.has(key)) {
          locationGroups.set(key, []);
        }
        locationGroups.get(key)?.push(article);
      }
    });
    
    console.log('Location groups:', locationGroups.size);

    // Add markers for each location
    locationGroups.forEach((locationArticles, key) => {
      const [lat, lng] = key.split(',').map(Number);
      const isSelected = selectedArticle ? locationArticles.some(a => a.id === selectedArticle.id) : false;
      
      const marker = L.marker([lat, lng], {
        icon: createCustomIcon(isSelected)
      }).addTo(mapInstanceRef.current!);

      // Create popup content
      const popupContent = document.createElement('div');
      popupContent.className = 'custom-popup';
      
      const locationName = locationArticles[0].location_name;
      popupContent.innerHTML = `
        <div class="popup-header">
          <h3>${locationName}</h3>
          <span class="article-count">${locationArticles.length} article${locationArticles.length > 1 ? 's' : ''}</span>
        </div>
        <div class="popup-articles">
          ${locationArticles.slice(0, 3).map(article => `
            <div class="popup-article" data-article-id="${article.id}">
              <h4>${article.title}</h4>
              <p>${article.excerpt.substring(0, 100)}...</p>
              <span class="popup-author">By ${article.author}</span>
            </div>
          `).join('')}
          ${locationArticles.length > 3 ? `
            <div class="popup-more">
              <span>+${locationArticles.length - 3} more article${locationArticles.length - 3 > 1 ? 's' : ''}</span>
            </div>
          ` : ''}
        </div>
      `;

      // Add click handlers for articles in popup
      popupContent.addEventListener('click', (e) => {
        const articleElement = (e.target as Element).closest('[data-article-id]') as HTMLElement;
        if (articleElement && onArticleNavigate) {
          const articleId = parseInt(articleElement.dataset.articleId || '0');
          const article = locationArticles.find(a => a.id === articleId);
          if (article) {
            onArticleNavigate(article);
          }
        }
      });

      marker.bindPopup(popupContent, {
        maxWidth: window.innerWidth < 480 ? window.innerWidth * 0.9 : 800,
        minWidth: window.innerWidth < 480 ? 250 : (window.innerWidth < 768 ? 300 : 500),
        className: 'history-popup',
        closeOnClick: false,
        autoClose: false,
        keepInView: true
      });

      // Only use click events to avoid hover conflicts with popup
      marker.on('click', () => {
        marker.openPopup();
        if (onArticleClick) {
          onArticleClick(locationArticles[0]);
        }
      });

      markersRef.current.push(marker);
    });

    // Don't auto-zoom - let user control the map view
  };

  return <div ref={mapRef} className="w-full h-full rounded-lg" style={{ height: 'calc(100vh - 240px)' }} />;
};

export default MapComponent;